import { useState } from 'react';
import { Plan } from "@/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Pencil, Trash2, Plus, RefreshCw, Check, X } from "lucide-react";
import { NetworkProvider, ContractLength, DataAllowance, RoamingType } from "@/types";
import { getAuthToken } from "@/utils/authUtils";

interface PlanManagementProps {
  plans: Plan[];
  isLoading: boolean;
  onRefresh: () => Promise<void>;
  onDeleteAll: () => Promise<void>;
}

export const PlanManagement = ({ plans, isLoading, onRefresh, onDeleteAll }: PlanManagementProps) => {
  const { toast } = useToast();
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  
  const [formValues, setFormValues] = useState<Partial<Plan>>({
    name: "",
    provider: "Telmore",
    price: 0,
    data_allowance: "1GB",
    contract_length: "Ingen binding",
    minutes: "Ubegrænset",
    texts: "Ubegrænset",
    roaming: "Ingen",
    has_5g: false,
    features: [],
    link: "",
    description: ""
  });

  const handleEditPlan = (plan: Plan) => {
    setSelectedPlan(plan);
    setFormValues({...plan, features: [...plan.features]});
    setIsEditing(true);
    setIsCreating(false);
  };

  const handleDeletePlan = async (planId: string) => {
    if (!confirm("Are you sure you want to delete this plan?")) {
      return;
    }

    setIsDeleting(planId);

    try {
      const token = await getAuthToken();
      
      const response = await fetch('https://rgrwileewnmhlqzdfzui.supabase.co/functions/v1/manage-plans', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          method: 'DELETE',
          action: 'delete',
          id: planId
        }),
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to delete plan');
      }

      toast({
        title: "Plan deleted",
        description: "The plan has been deleted successfully",
      });
      
      onRefresh();
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast({
        title: "Error deleting plan",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const handleCreatePlan = () => {
    setSelectedPlan(null);
    setFormValues({
      name: "",
      provider: "Telmore",
      price: 0,
      data_allowance: "1GB",
      contract_length: "Ingen binding",
      minutes: "Ubegrænset",
      texts: "Ubegrænset",
      roaming: "Ingen",
      has_5g: false,
      features: [],
      link: "",
      description: ""
    });
    setIsEditing(false);
    setIsCreating(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setIsCreating(false);
    setSelectedPlan(null);
  };

  const handleSavePlan = async () => {
    try {
      if (!formValues.name || !formValues.provider || formValues.price === undefined) {
        throw new Error('Please fill in all required fields');
      }

      setIsSaving(true);
      
      // Prepare the plan data
      const plan = {
        id: selectedPlan?.id || '',
        name: formValues.name || '',
        provider: formValues.provider as NetworkProvider,
        price: formValues.price || 0,
        price_label: formValues.price_label,
        original_price: formValues.original_price,
        data_allowance: formValues.data_allowance as DataAllowance,
        contract_length: formValues.contract_length as ContractLength,
        minutes: formValues.minutes || 'Unlimited',
        texts: formValues.texts || 'Unlimited',
        roaming: formValues.roaming as RoamingType,
        has_5g: !!formValues.has_5g,
        features: formValues.features || [],
        rating: formValues.rating || 0,
        review_count: formValues.review_count || 0,
        popularity: formValues.popularity || 0,
        link: formValues.link || '#',
        description: formValues.description,
        promo_code: formValues.promo_code,
        promo_ends: formValues.promo_ends
      };

      const method = isCreating ? 'POST' : 'PUT';
      const action = isCreating ? 'create' : 'update';
      const token = await getAuthToken();

      const response = await fetch('https://rgrwileewnmhlqzdfzui.supabase.co/functions/v1/manage-plans', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          method,
          action,
          plan
        }),
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.error || `Failed to ${isCreating ? 'create' : 'update'} plan`);
      }

      toast({
        title: isCreating ? "Plan created" : "Plan updated",
        description: isCreating 
          ? "The new plan has been created successfully" 
          : "The plan has been updated successfully",
      });
      
      onRefresh();
      setIsEditing(false);
      setIsCreating(false);
      setSelectedPlan(null);
    } catch (error) {
      console.error('Error saving plan:', error);
      toast({
        title: "Error saving plan",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleFeaturesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const features = e.target.value.split('\n').filter(feature => feature.trim() !== '');
    setFormValues(prev => ({
      ...prev,
      features
    }));
  };

  const providerOptions: NetworkProvider[] = [
    'Telmore', 'CBB', 'YouSee', 'Telenor', 'Eesy'
  ];

  const dataAllowanceOptions: DataAllowance[] = [
    '500MB', '1GB', '2GB', '3GB', '5GB', '10GB', '15GB', '20GB', '30GB', '50GB', '100GB', 'Ubegrænset'
  ];

  const contractLengthOptions: ContractLength[] = [
    'Ingen binding', '1 måned', '3 måneder', '6 måneder', '12 måneder', '18 måneder', '24 måneder'
  ];

  const roamingOptions: RoamingType[] = [
    'Ingen', 'EU', 'EU og udvalgte lande', 'Verden'
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Plans ({plans.length})</h2>
        <div className="flex gap-2">
          <Button 
            onClick={onDeleteAll} 
            variant="destructive"
            disabled={isLoading || plans.length === 0}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete All
          </Button>
          <Button onClick={onRefresh} variant="outline" disabled={isLoading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleCreatePlan} disabled={isEditing || isCreating}>
            <Plus className="h-4 w-4 mr-2" />
            Add Plan
          </Button>
        </div>
      </div>

      {isEditing || isCreating ? (
        <Card>
          <CardHeader>
            <CardTitle>{isCreating ? "Create New Plan" : "Edit Plan"}</CardTitle>
            <CardDescription>
              {isCreating 
                ? "Fill out the form below to create a new plan." 
                : "Edit the plan details below."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Plan Name</Label>
                <Input 
                  id="name"
                  name="name"
                  value={formValues.name || ''}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="provider">Provider</Label>
                <Select 
                  value={formValues.provider} 
                  onValueChange={(value) => handleSelectChange('provider', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {providerOptions.map(provider => (
                      <SelectItem key={provider} value={provider}>{provider}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="price">Price (kr)</Label>
                <Input 
                  id="price"
                  name="price"
                  type="number"
                  value={formValues.price || 0}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="original_price">Original Price (kr)</Label>
                <Input 
                  id="original_price"
                  name="original_price"
                  type="number"
                  value={formValues.original_price || ''}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="price_label">Price Label</Label>
                <Input 
                  id="price_label"
                  name="price_label"
                  value={formValues.price_label || ''}
                  onChange={handleInputChange}
                  placeholder="e.g. 99 kr./md i 6 måneder, derefter 149 kr./md"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="data_allowance">Data Allowance</Label>
                <Select 
                  value={formValues.data_allowance} 
                  onValueChange={(value) => handleSelectChange('data_allowance', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select data allowance" />
                  </SelectTrigger>
                  <SelectContent>
                    {dataAllowanceOptions.map(option => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="contract_length">Contract Length</Label>
                <Select 
                  value={formValues.contract_length} 
                  onValueChange={(value) => handleSelectChange('contract_length', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select contract length" />
                  </SelectTrigger>
                  <SelectContent>
                    {contractLengthOptions.map(option => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="minutes">Minutes</Label>
                <Input 
                  id="minutes"
                  name="minutes"
                  value={formValues.minutes || 'Ubegrænset'}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="texts">Texts</Label>
                <Input 
                  id="texts"
                  name="texts"
                  value={formValues.texts || 'Ubegrænset'}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="roaming">Roaming</Label>
                <Select 
                  value={formValues.roaming} 
                  onValueChange={(value) => handleSelectChange('roaming', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select roaming option" />
                  </SelectTrigger>
                  <SelectContent>
                    {roamingOptions.map(option => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2 flex items-center">
                <Label htmlFor="has_5g" className="mr-4">Has 5G</Label>
                <input 
                  id="has_5g"
                  name="has_5g"
                  type="checkbox"
                  checked={!!formValues.has_5g}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="link">Link</Label>
                <Input 
                  id="link"
                  name="link"
                  value={formValues.link || '#'}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea 
                  id="description"
                  name="description"
                  value={formValues.description || ''}
                  onChange={handleInputChange}
                  placeholder="Enter plan description..."
                  rows={3}
                />
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="features">Features (one per line)</Label>
                <Textarea 
                  id="features"
                  value={formValues.features?.join('\n') || ''}
                  onChange={handleFeaturesChange}
                  placeholder="Enter features, one per line..."
                  rows={5}
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={handleCancelEdit}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSavePlan} disabled={isSaving}>
              <Check className="h-4 w-4 mr-2" />
              {isSaving ? "Saving..." : "Save Plan"}
            </Button>
          </CardFooter>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map(plan => (
            <Card key={plan.id} className="overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="flex justify-between items-start">
                  <span>{plan.name}</span>
                  <span className="text-lg font-normal">{plan.price} kr</span>
                </CardTitle>
                <CardDescription>{plan.provider}</CardDescription>
              </CardHeader>
              <CardContent className="pb-2">
                <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                  <div>
                    <strong>Data:</strong> {plan.data_allowance}
                  </div>
                  <div>
                    <strong>Contract:</strong> {plan.contract_length}
                  </div>
                  <div>
                    <strong>5G:</strong> {plan.has_5g ? 'Yes' : 'No'}
                  </div>
                  <div>
                    <strong>Roaming:</strong> {plan.roaming}
                  </div>
                </div>
                <div className="text-sm mb-2">
                  <strong>Features:</strong>
                  <ul className="list-disc list-inside">
                    {plan.features.slice(0, 3).map((feature, index) => (
                      <li key={index} className="truncate">{feature}</li>
                    ))}
                    {plan.features.length > 3 && <li>+ {plan.features.length - 3} more</li>}
                  </ul>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end gap-2 pt-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDeletePlan(plan.id)}
                  disabled={isDeleting === plan.id || isLoading}
                >
                  {isDeleting === plan.id ? (
                    <span className="h-4 w-4 animate-spin">⏳</span>
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditPlan(plan)}
                  disabled={isLoading}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
